import React, { useState } from "react";
import Heading from "carbon-react/lib/components/heading";
import Dialog from "carbon-react/lib/components/dialog";
import RequestResetStep from "./RequestResetStep";
import IssuePasswordStep from "./IssuePasswordStep";
import ErrorMessage from "../../messages/ErrorMessage";
import { useAppContext } from "../../../contexts/AppContext";
import { useUserApi } from "../../../services/UserApi";

import "./ResetPasswordDialog.css";

const getInitialRequestState = () => {
  return {
    languageCode: null,
    languageCodeValid: true,
    agentComments: "",
    agentCommentsValid: true,
  };
};

const getInitialIssuePasswordState = () => {
  return {
    password: null,
  };
};

const steps = Object.freeze({
  requestReset: "requestReset",
  issuePassword: "issuePassword",
});

const ResetPasswordDialog = ({ user, open, onClose }) => {
  const [busy, setBusy] = useState(false);
  const [currentStep, setCurrentStep] = useState(steps.requestReset);
  const [requestResetState, setRequestResetState] = useState(
    getInitialRequestState()
  );
  const [issuePasswordState, setIssuePasswordState] = useState(
    getInitialIssuePasswordState()
  );
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const { toastSuccess } = useAppContext();
  const { resetPassword } = useUserApi();

  const resetState = () => {
    clearDialogError();
    setRequestResetState(getInitialRequestState());
    setIssuePasswordState(getInitialIssuePasswordState());
    setCurrentStep(steps.requestReset);
  };

  const onDialogClose = () => {
    onClose();
    resetState();
  };

  const showDialogError = (msg) => {
    const sanistizedMsg =
      msg && msg.length > 0 ? msg.trim() : "Operation failed";
    setErrorMsg(sanistizedMsg);
    setShowError(true);
  };

  const clearDialogError = () => {
    setErrorMsg("");
    setShowError(false);
  };

  const onDialogCancel = () => {
    onDialogClose();
  };

  const onRequestResetCompleted = async () => {
    try {
      setBusy(true);
      clearDialogError();

      const apiResult = await resetPassword(
        user.userId,
        requestResetState.languageCode,
        requestResetState.agentComments
      );

      if (!apiResult?.success)
        throw new Error(
          apiResult?.error?.description || "Failed to complete email change"
        );

      if (!apiResult?.data?.password || apiResult.data.password.length < 1)
        throw new Error("Unexpected response.");

      toastSuccess(
        `Password reset completed. Notification sent to ${user?.email}`
      );

      setIssuePasswordState({ password: apiResult.data.password });
      setCurrentStep(steps.issuePassword);
    } catch (error) {
      showDialogError(error?.toString());
    }
    setBusy(false);
  };

  const heading = <Heading title="Reset password" />;

  const requestResetBody = (
    <RequestResetStep
      user={user}
      busy={busy}
      state={requestResetState}
      onStateChanged={setRequestResetState}
      onCancel={onDialogCancel}
      onCompleted={onRequestResetCompleted}
    />
  );

  const issuePasswordBody = (
    <IssuePasswordStep
      busy={busy}
      state={issuePasswordState}
      onClose={onDialogClose}
    />
  );

  const stepBody = (
    <>
      {currentStep === steps.requestReset
        ? requestResetBody
        : issuePasswordBody}
    </>
  );

  const errorMessage = (
    <>
      {showError !== true ? null : (
        <ErrorMessage
          message={
            errorMsg && errorMsg.length > 0 ? errorMsg : "The operation failed"
          }
        />
      )}
    </>
  );

  return (
    <>
      {!user ? null : (
        <>
          <Dialog open={open} showCloseIcon={true} onCancel={onDialogCancel}>
            {heading}
            {stepBody}
            {errorMessage}
          </Dialog>
        </>
      )}
    </>
  );
};

export default ResetPasswordDialog;
